import { Typography, Box, Divider } from '@mui/material';
import PatientDetailsCard from 'components/PatientDetailsCard';
import UserAddedToolTip from 'pages/Dashboard/components/UserAddedToolTip';
import HealthRecordCategories from 'pages/Dashboard/HealthRecordCategories';
import {
  ShowMoreDrawerChildProps,
  sliceForShowMore,
} from 'pages/Dashboard/ShowMoreDrawer';
import { SourceTypeMap } from 'pages/Dashboard/types';
import {
  MentalHealthProvider,
  MentalHealthProviderCategory,
  Provider,
} from 'pages/Dashboard/types/providers.types';
import * as React from 'react';
import { Link } from 'react-router-dom';
import theme from 'theme';
import { EProfessions } from 'types/Entitites';

export interface ProviderCardProps extends ShowMoreDrawerChildProps {
  gridSize?: number;
  patientProviders?: Provider[];
}

const providerCategoryMap: { [key: string]: string } = {
  [MentalHealthProviderCategory.MENTAL_HEALTH_PROVIDER_CATEGORY_PSYCHIATRIST]: 'Psychiatrist',
  [MentalHealthProviderCategory.MENTAL_HEALTH_PROVIDER_CATEGORY_THERAPIST]: 'Therapist',
  [MentalHealthProviderCategory.MENTAL_HEALTH_PROVIDER_CATEGORY_UNSPECIFIED]: 'Other Provider',
};

function getTaxonomyText(currentProvider: MentalHealthProvider): string {
  let taxonomyText = '';

  if (currentProvider.source !== SourceTypeMap.SOURCE_TYPE_PATIENT_REPORTED
    && currentProvider.provider.taxonomyGroup) {
    taxonomyText = currentProvider.provider.taxonomyGroup;
  }
  else if (currentProvider.source === SourceTypeMap.SOURCE_TYPE_PATIENT_REPORTED
    && currentProvider.category
    === MentalHealthProviderCategory.MENTAL_HEALTH_PROVIDER_CATEGORY_UNSPECIFIED
  ) {
    taxonomyText = 'PCP';
  }
  else if (providerCategoryMap?.[currentProvider.category as string]) {
    taxonomyText = providerCategoryMap[currentProvider.category as string];
  }
  else if (currentProvider.provider.designation) {
    taxonomyText = EProfessions[currentProvider.provider.designation as keyof typeof EProfessions];
  }
  else {
    taxonomyText = '';
  }
  return taxonomyText;
}

function ProviderDetailItem({
  currentProvider,
}: {
  currentProvider: MentalHealthProvider;
}): JSX.Element {
  const hasNPINumber = currentProvider?.provider?.npiNumber > 0;
  const lookupURL = `${process.env.REACT_APP_NPI_LOOKUP_BASE_URL}${currentProvider.provider.npiNumber}`;
  const providerNameColor = hasNPINumber
    ? theme.custom.colors.primaryMain
    : '';
  let taxonomyText = getTaxonomyText(currentProvider);
  const { provider: { licenseState } } = currentProvider;
  if (licenseState) {
    taxonomyText += ` - ${licenseState}`;
  }
  const providerName = [
    currentProvider.provider.namePrefix,
    currentProvider.provider.firstName,
    currentProvider.provider.lastName,
    currentProvider.provider.credential,
  ]
    .filter(Boolean) // Removes falsy values (e.g., undefined, null, empty string)
    .join(' ');
  return (
    <React.Fragment key={`health-history-${currentProvider.provider.id}`}>
      <Box display='flex' justifyContent='space-between' width='100%'>
        <Box display='flex' flexDirection='column'>
          {hasNPINumber ? (
            <Link
              to={lookupURL}
              target='_blank'
              rel='noopener'
              style={{ textDecoration: 'none' }}
            >
              <Typography
                color={providerNameColor}
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {providerName}
              </Typography>
            </Link>
          ) : (
            <Typography variant='body1' color={providerNameColor}>
              {providerName}
            </Typography>
          )}
          <Typography variant='body2' color='text.secondary'>
            {taxonomyText}
          </Typography>
        </Box>
        <UserAddedToolTip source={currentProvider?.source} />
      </Box>
    </React.Fragment>
  );
}

export default function ProvidersCard({
  gridSize,
  drawerMode = false,
  patientProviders = [],
  onShowMore,
}: ProviderCardProps): JSX.Element | null {
  const allProviders: Array<Provider & { category?: string }> = [
    ...patientProviders,
  ];

  const data = sliceForShowMore(allProviders, !drawerMode, 6);
  return (
    <PatientDetailsCard
      title={HealthRecordCategories.providers.value.displayName}
      showHeader={!drawerMode && allProviders?.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && data.length > 6 && !!onShowMore}
      onViewDetails={onShowMore as VoidFunction}
      key='provider-card'
      showSubHeader={false}
    >
      {data.map((currentProvider, idx) => (
        <React.Fragment key={currentProvider.id}>
          <ProviderDetailItem
            currentProvider={currentProvider as MentalHealthProvider}
          />
          {idx < data.length - 1 && <Divider sx={{ borderColor: theme.custom.colors.borderColor, width: '100%', my: 1 }} />}
        </React.Fragment>
      ))}
    </PatientDetailsCard>
  );
}
